import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * ForgotPassword — front-end shell for the password-reset flow.
 *
 * Posts to `POST /api/auth/forgot-password` when the endpoint exists.
 * Until then the success state is rendered locally so the page works
 * end-to-end from a UX perspective (security best practice: always
 * show the same "if an account exists, we've sent a link" copy
 * whether or not the email is on file — so we don't leak membership).
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            // Best-effort call; backend endpoint will ship separately.
            const base = process.env.REACT_APP_BACKEND_URL || "";
            await fetch(`${base}/api/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
                credentials: "include",
            }).catch(() => {});
            setSent(true);
        } catch (ex) {
            setErr(ex.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#03122b] px-6 text-white"
            data-testid="forgot-page"
        >
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-[0.12]" />
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#00C2FF]/12 blur-[160px]" />
            <div className="pointer-events-none absolute -right-40 bottom-1/4 h-[420px] w-[420px] rounded-full bg-[#5b5ef0]/14 blur-[160px]" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 flex items-center justify-center">
                    <img
                        src="/brand/moovbytes-logo-white.png"
                        alt="MoovBytes"
                        className="h-8 w-auto select-none"
                    />
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-8">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />
                    {sent ? (
                        <>
                            <h1 className="font-display text-2xl font-bold tracking-tight">
                                Check your inbox
                            </h1>
                            <p className="mt-2 text-sm text-white/65">
                                If an account exists for{" "}
                                <span className="font-semibold text-white">{email}</span>, we’ve
                                sent a reset link. It will expire in 30 minutes.
                            </p>
                            <Link
                                to="/login"
                                data-testid="forgot-back-to-login"
                                className="mt-7 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 transition hover:text-white"
                            >
                                <ArrowLeft size={12} /> Back to log in
                            </Link>
                        </>
                    ) : (
                        <>
                            <h1 className="font-display text-2xl font-bold tracking-tight">
                                Reset your password
                            </h1>
                            <p className="mt-2 text-sm text-white/55">
                                Enter the email you signed up with. We’ll send a reset link.
                            </p>

                            <form onSubmit={submit} className="mt-6" data-testid="forgot-form">
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-white/45">
                                        Email address
                                    </span>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        placeholder="you@company.com"
                                        data-testid="forgot-email-input"
                                        className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20"
                                    />
                                </label>

                                {err && (
                                    <div
                                        data-testid="forgot-error"
                                        className="mt-3 rounded-lg border border-[#ef4444]/40 bg-[#ef4444]/10 px-3 py-2 text-xs text-[#fca5a5]"
                                    >
                                        {err}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="forgot-submit-btn"
                                    className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-white px-3 py-3 text-sm font-semibold text-[#03122b] shadow-lg transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {loading ? "Sending…" : "Send reset link"}
                                </button>
                            </form>

                            <Link
                                to="/login"
                                data-testid="forgot-back-link"
                                className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition hover:text-white"
                            >
                                <ArrowLeft size={12} /> Back to log in
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
